import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, Send, ExternalLink } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// Conectar um bot do Telegram.
//
// Só há uma credencial: o token que o @BotFather entrega. Não existe login
// nem QR — o servidor confere o token, registra o webhook e a conexão já
// nasce recebendo. Por isso o formulário é uma linha só.

const TelegramConnectModal = ({ open, onClose }) => {
	const [token, setToken] = useState("");
	const [name, setName] = useState("");
	const [salvando, setSalvando] = useState(false);

	const fechar = useCallback(() => {
		setToken("");
		setName("");
		setSalvando(false);
		onClose();
	}, [onClose]);

	const salvar = async (e) => {
		e.preventDefault();
		setSalvando(true);

		try {
			await api.post("/whatsapp/telegram", {
				token: token.trim(),
				name: name.trim() || undefined,
			});

			toast.success("Bot conectado. O webhook já está registrado.");
			fechar();
		} catch (err) {
			toastError(err);
		} finally {
			setSalvando(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && fechar()}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Send className="h-5 w-5 text-[#229ED9]" />
						Conectar bot do Telegram
					</DialogTitle>
					<DialogDescription>
						As conversas do bot passam a cair na tela de Atendimentos, com fila,
						chatbot e fluxo como qualquer outra conexão.
					</DialogDescription>
				</DialogHeader>

				<form onSubmit={salvar} className="space-y-4">
					<div className="space-y-1.5">
						<Label htmlFor="telegram-token">Token do bot</Label>
						<Input
							id="telegram-token"
							value={token}
							autoComplete="off"
							placeholder="1234567890:AAF..."
							onChange={(ev) => setToken(ev.target.value)}
						/>
						<p className="text-xs leading-relaxed text-muted-foreground">
							Fale com o{" "}
							<a
								href="https://t.me/BotFather"
								target="_blank"
								rel="noreferrer"
								className="inline-flex items-center gap-1 underline underline-offset-2"
							>
								@BotFather
								<ExternalLink className="h-3 w-3" />
							</a>{" "}
							no Telegram, mande <code>/newbot</code> e cole aqui o token que ele
							responder. Guarde-o: quem tiver esse token responde em nome do bot.
						</p>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="telegram-nome">
							Nome da conexão
							<span className="ml-1 font-normal text-muted-foreground">
								(opcional)
							</span>
						</Label>
						<Input
							id="telegram-nome"
							value={name}
							autoComplete="off"
							onChange={(ev) => setName(ev.target.value)}
						/>
						<p className="text-xs leading-relaxed text-muted-foreground">
							Em branco, usa o nome do próprio bot.
						</p>
					</div>

					<div className="rounded-lg border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
						O Telegram só entrega mensagem em endereço <b>HTTPS</b>. Se o
						servidor deste sistema estiver em HTTP, o bot é aceito e nada chega
						— o cadastro vai avisar antes de gravar.
					</div>

					<DialogFooter>
						<Button type="button" variant="outline" onClick={fechar}>
							Cancelar
						</Button>
						<Button type="submit" disabled={!token.trim() || salvando}>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							Conectar
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default TelegramConnectModal;
